import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiSend } from '../utils/api';

// CSV templates (header row only) — match the importer's expected headers in routes/deals.js.
const TEMPLATES = {
  available: {
    label: 'Available deals',
    file: 'available_deals_template.csv',
    headers: ['address', 'zip', 'asking_price', 'summary', 'arv', 'coe_raw', 'photos_link', 'stage'],
  },
  closings: {
    label: 'Closed / Sold deals',
    file: 'closed_sold_deals_template.csv',
    headers: ['address', 'zip', 'title_company', 'sale_price', 'earnest_money', 'acquisitions_agent',
      'dispositions_agent', 'fee', 'close_date', 'close_month', 'notes', 'type', 'buyer', 'stage'],
  },
};

function downloadTemplate(t) {
  const blob = new Blob([t.headers.join(',') + '\n'], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = t.file; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

const STAGE_TINT = { Available: '#6B6760', Sold: '#4A7C59', Closed: '#2D4C38' };
const card = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 20 };
const btnPrimary = { padding: '9px 16px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost = { padding: '9px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };

export default function ImportDeals() {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose');      // choose | preview | done
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  function onFile(e) {
    const f = e.target.files && e.target.files[0];
    setError(null);
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(f);
  }

  async function run(mode) {
    setBusy(true); setError(null);
    try {
      const data = await apiSend(`/api/deals/import?mode=${mode}`, {
        method: 'POST', body: csvText, headers: { 'Content-Type': 'text/csv' },
      });
      if (mode === 'preview') { setPreview(data); setStep('preview'); }
      else { setResult(data); setStep('done'); }
    } catch (e) {
      setError(e.message || 'Import failed.');
    }
    setBusy(false);
  }

  function reset() {
    setStep('choose'); setFileName(''); setCsvText(''); setPreview(null); setResult(null); setError(null);
  }

  return (
    <div style={{ maxWidth: 880 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 6 }}>Import Deals</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Bring your existing book of deals in from a CSV. Imported deals never send email or SMS.</p>
        </div>
        <button onClick={() => navigate('/pipeline')} style={btnGhost}>← Pipeline</button>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['Choose CSV', 'Preview', 'Done'].map((label, i) => {
          const idx = { choose: 0, preview: 1, done: 2 }[step];
          const active = i === idx, past = i < idx;
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600,
              color: active ? 'var(--teal)' : past ? 'var(--gray-300)' : 'var(--gray-500)' }}>
              <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: active || past ? 'var(--teal)' : 'var(--navy-border)', color: active || past ? '#fff' : 'var(--gray-500)', fontSize: 11 }}>{i + 1}</span>
              {label}{i < 2 && <span style={{ color: 'var(--navy-border)', marginLeft: 4 }}>→</span>}
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ ...card, padding: 14, marginBottom: 16, borderColor: '#B85450', background: 'rgba(184,84,80,0.08)', color: '#B85450', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* STEP 1 — choose */}
      {step === 'choose' && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', marginBottom: 10 }}>1. Choose a CSV file</div>
          <input type="file" accept=".csv,text/csv" onChange={onFile}
            style={{ fontSize: 13, color: 'var(--gray-300)', marginBottom: 6 }} />
          {fileName && <div style={{ fontSize: 12, color: 'var(--teal)', marginBottom: 10 }}>Selected: {fileName}</div>}
          <div style={{ marginTop: 14 }}>
            <button onClick={() => run('preview')} disabled={!csvText || busy} style={{ ...btnPrimary, opacity: (!csvText || busy) ? 0.5 : 1 }}>
              {busy ? 'Reading…' : 'Preview import'}
            </button>
          </div>

          <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--navy-border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Need the format? Download a template</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {Object.values(TEMPLATES).map(t => (
                <button key={t.file} onClick={() => downloadTemplate(t)} style={btnGhost}>↓ {t.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STEP 2 — preview */}
      {step === 'preview' && preview && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', marginBottom: 14 }}>2. Preview — {preview.total_rows} rows in “{fileName}”</div>

          {/* counts */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {Object.entries(preview.by_stage || {}).map(([stage, n]) => (
              <div key={stage} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--navy-border)', minWidth: 110 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: STAGE_TINT[stage] || 'var(--white)' }}>{n}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{stage}</div>
              </div>
            ))}
            <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--teal)', background: 'var(--teal-dim)', minWidth: 110 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--teal)' }}>{preview.to_insert}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Will import</div>
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--navy-border)', minWidth: 110 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-300)' }}>{preview.duplicate_count}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Skipped (dupes)</div>
            </div>
          </div>

          {preview.unknown_columns && preview.unknown_columns.length > 0 && (
            <div style={{ fontSize: 12, color: '#E0A526', marginBottom: 12 }}>
              Unrecognized columns (ignored): {preview.unknown_columns.join(', ')}
            </div>
          )}

          {/* flagged rows */}
          {preview.flagged && preview.flagged.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
                ⚠ {preview.flagged_count} row{preview.flagged_count === 1 ? '' : 's'} need a look
              </div>
              <div style={{ border: '1px solid var(--navy-border)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--navy-light)', color: 'var(--gray-500)', textAlign: 'left' }}>
                      <th style={{ padding: '7px 10px' }}>Address</th><th style={{ padding: '7px 10px' }}>Stage</th>
                      <th style={{ padding: '7px 10px' }}>Flag / warning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.flagged.map((r, i) => (
                      <tr key={i} style={{ background: 'rgba(224, 165, 38,0.06)', borderTop: '1px solid var(--navy-border)' }}>
                        <td style={{ padding: '7px 10px', color: 'var(--white)' }}>{r.address || '(missing)'}{r.zip ? ` · ${r.zip}` : ''}</td>
                        <td style={{ padding: '7px 10px', color: 'var(--gray-300)' }}>{r.stage || '—'}</td>
                        <td style={{ padding: '7px 10px', color: '#E0A526' }}>{[r.review_flags, ...(r.warnings || [])].filter(Boolean).join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={() => run('commit')} disabled={busy || preview.to_insert === 0} style={{ ...btnPrimary, opacity: (busy || preview.to_insert === 0) ? 0.5 : 1 }}>
              {busy ? 'Importing…' : `Confirm import (${preview.to_insert})`}
            </button>
            <button onClick={reset} style={btnGhost}>Choose another file</button>
          </div>
        </div>
      )}

      {/* STEP 3 — done */}
      {step === 'done' && result && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#4A7C59', marginBottom: 8 }}>✓ Import complete</div>
          <div style={{ fontSize: 14, color: 'var(--gray-300)', marginBottom: 18 }}>
            Imported <strong style={{ color: 'var(--white)' }}>{result.inserted}</strong> deal{result.inserted === 1 ? '' : 's'}
            {result.skipped_duplicates > 0 && <> · skipped <strong style={{ color: 'var(--white)' }}>{result.skipped_duplicates}</strong> duplicate{result.skipped_duplicates === 1 ? '' : 's'}</>}.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/pipeline')} style={btnPrimary}>Go to pipeline</button>
            <button onClick={reset} style={btnGhost}>Import another file</button>
          </div>
        </div>
      )}
    </div>
  );
}
